import axios from 'axios';
import {
  Badge,
  Card,
  CardHeader,
  ContentLoader,
  getStatusBadgeVariant,
} from '@/components/common';
import { useAuthStore } from '@/store/auth';
import { useMyPOSP } from '@/hooks/useBrokers';
import { titleCase } from '@/utils';
// Both panels are self-contained and are simply relocated: the same component
// the broker-side channel page renders, so the two views cannot drift.
import { PospOnboardingPanel, PospRequiredDocumentsSection } from '@/pages/brokers/PospOnboardingPanel';
import { PospBankDetailsPanel } from '@/pages/brokers/PospBankDetailsPanel';
// P1 — the SAME card the Broker Admin maintains on the channel detail page,
// with `canManage={false}`. One component over one endpoint, so the agent's
// Certifications section and their brokerage's cannot disagree about whether a
// certificate has expired.
import { PospTrainingPanel } from '@/pages/brokers/PospTrainingPanel';
// POSP #2 — the account profile, the SAME component every other role's Profile
// tab mounts. Merged, not copied: one form, one save path, and the avatar
// uploader an agent previously had to leave this page to reach.
import { AccountProfileSection } from './AccountProfileSection';
// P1 / P4 — the two agent-owned sections. Both are self-contained cards with
// their own loading / error / empty states, kept out of this file so the
// section stays a layout.
import { PospBrokerMappingCard } from './PospBrokerMappingCard';
import { PospContactSyncCard } from './PospContactSyncCard';
import { PospCoverPreferenceCard } from './PospCoverPreferenceCard';
import { TelegramConnectCard } from './TelegramConnectCard';

/**
 * P2 / decision 16 — the POSP's agent profile, as the body of `/settings` ›
 * Profile.
 *
 * This WAS `PospSettingsPage`, a second settings screen reached from a "My
 * Account › My Agent Profile" link below the tab list. An agent therefore had
 * two profiles to keep straight: a Profile tab holding their name, phone and
 * avatar, and a separate page holding everything that makes them an agent. The
 * split was the reason the same `User` row used to be printed on both.
 *
 * So the page is gone and its body is this component, mounted inside the
 * Profile tab. `/settings/agent` redirects to `/settings` for old links.
 *
 * The order is the decision's, and it is the order an agent reads themselves
 * in: who they are, where their application stands, what they are certified
 * for, where their money goes, who they sell for, what they still owe.
 *
 *   1. account details   — `AccountProfileSection` (editable, shared)
 *   2. status/onboarding — the status badge and the activation stepper
 *   3. certifications    — IRDAI, training, examination (read-only)
 *   4. bank details      — where commission payouts are sent
 *   5. broker mapping    — the request their brokerage answers
 *   6. documents         — their KYC uploads and the review they trigger
 *
 * Contact Sync (P4) has no place in that list because the decision does not
 * name it; it keeps its old neighbours — it is an agent-owned card like the
 * mapping above it — and sits between mapping and documents.
 *
 * ACCOUNT DETAILS ARE NOT BEHIND THE AGENT FETCH. Section 1 is the only editor
 * an agent has for their own name, phone and avatar, and this tab is now the
 * only place it exists. `/brokers/posps/me/` 404s for a `role="posp"` user with
 * no POSP row (five such logins exist in production), so gating the whole tab
 * on that fetch would leave exactly those people unable to edit their own name.
 * Only sections 2-6 are the agent row's; they degrade on their own.
 *
 * CERTIFICATIONS WAS TWO CARDS on the old `POSPDetailPage` ("Certifications"
 * in the main column and "Certification" in the right rail, both printing the
 * IRDAI number and expiry), and then a third hand-rolled grid here. All three
 * are now one `PospTrainingPanel` (P1), read-only for the agent: the superset
 * of every field they carried, plus the stage, hours and expiry countdown that
 * only the compliance record holds.
 *
 * Nothing here changes what a POSP may edit: every card is the one it was, with
 * the same props and the same permission checks.
 */
export function PospProfileSection() {
  const { data: posp, isLoading, error, refetch } = useMyPOSP();
  const authUserId = useAuthStore((state) => state.user?.id);
  const isNoLinkedProfile = axios.isAxiosError(error) && error.response?.status === 404;

  return (
    <div className="space-y-6">
      {/* 1. Account details — one card, editable, and the SAME component every
          other role's Profile tab mounts. It replaced the old read-only
          "Policy Agent" + "Contact Information" pair, which printed the agent's
          phone and email twice off one User row.

          Rendered before the fetch is resolved and regardless of how it
          resolves: it reads `/users/me/`, not the agent row, and it is the only
          way an agent can edit their own name. See the note above. */}
      <AccountProfileSection
        infoTitle="Contact Information"
        infoSubtitle="Your details as your customers and your brokerage see them"
        infoFootnote="Your customers see this name, phone and email as their policy agent."
      />

      {isLoading && <ContentLoader />}

      {/* A failed fetch is reported as a failure, never as an empty profile --
          and it costs the agent sections 2-6 only, not the card above.

          The 404 is its own case, as it is in CustomerProfileSection: the
          endpoint 404s on `hasattr(request.user, "posp")`, so a `role="posp"`
          login with no agent row lands here every time. That is not transient
          and "Try again" would be a lie to them; only a real failure gets the
          retry. */}
      {!isLoading && (error || !posp) && (
        <Card>
          <div className="text-center py-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile unavailable</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              {isNoLinkedProfile || !error
                ? 'No agent profile is linked to this login, so there is nothing to show here yet. Please contact your broker. Your account details above are unaffected.'
                : 'Your agent profile could not be loaded. Your account details above are unaffected.'}
            </p>
            {error && !isNoLinkedProfile && (
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Try again
              </button>
            )}
          </div>
        </Card>
      )}

      {!isLoading && !error && posp && (
        <>
        {/* 2. Status and onboarding. The agent code lives here now that the page
            header that carried it is gone -- labelled, because a bare "PSP001"
            under a title was an unexplained string. */}
        <Card>
          <CardHeader title="Status" />
          <dl className="space-y-3">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500 dark:text-gray-400">Current Status</dt>
              <dd>
                <Badge variant={getStatusBadgeVariant(posp.status)}>{titleCase(posp.status)}</Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500 dark:text-gray-400">Agent Code</dt>
              <dd className="text-sm font-medium text-gray-900 dark:text-white">{posp.posp_code}</dd>
            </div>
          </dl>
        </Card>

        {/* Messaging Plan 2: which covers their quotes for customers price (Telegram / MCP). */}
        <PospCoverPreferenceCard />

        {/* "Connect my Telegram": for an agent whose Telegram number is not the one on their record. */}
        <TelegramConnectCard />

        {/* Activation Progress (+ the broker-side review controls, which this
            panel already hides from a POSP). Required Documents is the panel's
            other half and is rendered last instead -- see the order above. */}
        <PospOnboardingPanel pospId={posp.id} includeDocuments={false} />

        {/* 3. Certifications — READ-ONLY, and the same component the Broker
            Admin maintains on the channel detail page rather than a second
            hand-rolled grid over the same columns (P1). It shows what a grid
            over `posp` could not, because nothing recorded it: the compliance
            stage, the training hours, and an expiry that is counted rather than
            merely printed. An agent cannot certify themselves, and the server
            refuses them even if a control were drawn. */}
        <PospTrainingPanel pospId={posp.id} canManage={false} title="Certifications" />

        {/* 4. POSP-1: the account commission payouts are sent to. Masked here,
            full only inside the edit form, and only for its owner —
            `PayoutProcessor.create_payout` snapshots exactly these columns, so
            this is the card that decides where the money goes. */}
        <PospBankDetailsPanel
          pospId={posp.id}
          bankName={posp.bank_name}
          ifscCode={posp.bank_ifsc_code}
          maskedAccountNumber={posp.bank_account_number_masked}
          canEdit={Boolean(authUserId && posp.user && posp.user === authUserId)}
        />

        {/* 5. P1 / M-03. "Map to My Broker", but as a request the brokerage
            answers, not a switch. See PospBrokerMappingCard for why. */}
        <PospBrokerMappingCard
          currentBrokerName={posp.broker_name}
          currentBrokerCode={posp.broker_code}
          currentBrokerLogoUrl={posp.broker_logo_url}
        />

        {/* P4. The agent's own synced phone contacts. No broker-side counterpart
            exists, by design. */}
        <PospContactSyncCard />

        {/* 6. Required Documents, and the button that sends them for review. */}
        <PospRequiredDocumentsSection pospId={posp.id} pospStatus={posp.status} />
        </>
      )}
    </div>
  );
}
